import { Building2, Pencil, Search, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { childrenOf } from '../lib/departmentTree'
import type { Department } from '../types'
import { StatusBadge } from './AccessBits'

type Props = {
  department: Department
  allDepartments: Department[]
  canWrite: boolean
  onEdit: () => void
  onSelectDepartment: (department: Department) => void
}

export function DepartmentDetailPanel({ department, allDepartments, canWrite, onEdit, onSelectDepartment }: Props) {
  const { t } = useTranslation()
  const [tab, setTab] = useState('members')
  const [memberQ, setMemberQ] = useState('')
  const children = useMemo(() => childrenOf(department.id, allDepartments), [department.id, allDepartments])
  const members = useMemo(() => {
    const term = memberQ.trim().toLocaleLowerCase('tr')
    return department.members.filter((m) => !term || `${m.name} ${m.email}`.toLocaleLowerCase('tr').includes(term))
  }, [department.members, memberQ])

  return (
    <section className="overflow-hidden rounded-2xl border bg-card">
      <div className="flex flex-wrap items-start gap-3 border-b p-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
          <Building2 className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{department.name}</h2>
            <StatusBadge status={department.status} />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{department.description || t('access.departments.detail.noDescription')}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{department.code}</p>
        </div>
        {canWrite ? (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil />
            {t('common.edit')}
          </Button>
        ) : null}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="border-b px-4 pt-2">
          <TabsList>
            <TabsTrigger value="members">{t('access.departments.tabs.members', { count: department.memberCount })}</TabsTrigger>
            <TabsTrigger value="children">{t('access.departments.tabs.children', { count: children.length })}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="members" className="p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{t('access.departments.detail.membersTitle', { count: department.memberCount })}</h3>
            <div className="relative ml-auto w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={memberQ} onChange={(e) => setMemberQ(e.target.value)} placeholder={t('access.departments.detail.memberSearch')} className="pl-8" />
            </div>
          </div>
          {members.length === 0 ? (
            <EmptyState icon={Users} title={t('access.departments.detail.membersEmpty')} />
          ) : (
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('access.departments.detail.columns.name')}</TH>
                  <TH className="hidden sm:table-cell">{t('access.departments.detail.columns.email')}</TH>
                  <TH>{t('access.departments.detail.columns.role')}</TH>
                </TR>
              </THead>
              <TBody>
                {members.map((m) => (
                  <TR key={m.id}>
                    <TD>
                      <div className="flex items-center gap-2">
                        <Avatar name={m.name} src={m.avatarUrl} className="size-8" />
                        <span className="font-medium">{m.name}</span>
                      </div>
                    </TD>
                    <TD className="hidden text-muted-foreground sm:table-cell">{m.email || '—'}</TD>
                    <TD>
                      <Badge tone={m.id === department.managerId ? 'primary' : 'neutral'}>
                        {m.id === department.managerId ? t('access.departments.detail.managerBadge') : t('access.departments.detail.memberBadge')}
                      </Badge>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="children" className="p-4">
          <h3 className="mb-3 text-sm font-semibold">{t('access.departments.detail.childrenTitle', { count: children.length })}</h3>
          {children.length === 0 ? (
            <EmptyState icon={Building2} title={t('access.departments.detail.childrenEmpty')} />
          ) : (
            <ul className="divide-y rounded-xl border">
              {children.map((c) => (
                <li key={c.id}>
                  <button type="button" className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50" onClick={() => onSelectDepartment(c)}>
                    <Building2 className="size-4 text-brand-blue" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{c.name}</span>
                      <span className="block font-mono text-xs text-muted-foreground">{c.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{t('access.departments.memberCount', { count: c.memberCount })}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </section>
  )
}
